import React, { useState, useEffect } from 'react';
import { renderTimeline } from '../../services/renderService';
import { startCloudRender, pollRenderUntilComplete, saveProjectToBackend, CONFIG } from '../../services/backendService';
import { CloseIcon } from '../icons/CloseIcon';
import { LoadingSpinner } from '../icons/LoadingSpinner';
import { MagicWandIcon } from '../icons/MagicWandIcon';
import { DownloadIcon } from '../icons/DownloadIcon';
import { FilmIcon } from '../icons/FilmIcon';
import { SaveIcon } from '../icons/SaveIcon';
import { v4 as uuidv4 } from 'uuid';
import { useUser } from '../../context/UserContext';

export const RenderModal = ({ project, onClose, onProjectUpdate }) => {
    const [status, setStatus] = useState('idle');
    const [progress, setProgress] = useState(0);
    const [message, setMessage] = useState('');
    const [videoUrl, setVideoUrl] = useState(null);
    const [error, setError] = useState(null);
    const [isSavingToProject, setIsSavingToProject] = useState(false);
    
    // Get authenticated user from context
    const { user } = useUser();

    // Get user email from multiple sources with proper priority
    const userEmail = user?.email || 
                      (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')).email : null) || 
                      'guest@loopai.com';
    
    // Debug: Log the user email being used
    useEffect(() => {
        console.log('🎬 RenderModal - User email:', userEmail);
    }, [userEmail]);

    useEffect(() => {
        return () => {
            if (videoUrl && videoUrl.startsWith('blob:')) {
                URL.revokeObjectURL(videoUrl);
            }
        };
    }, [videoUrl]);

    const handleLocalRender = async () => {
        if (!project.timeline || project.timeline.clips.length === 0) {
            setError("Cannot render an empty timeline.");
            setStatus('error');
            return;
        }

        setStatus('rendering');
        setError(null);
        setProgress(0);
        setMessage('Initializing render engine...');

        try {
            const url = await renderTimeline(project, (p, m) => {
                setProgress(p);
                setMessage(m);
            });
            setVideoUrl(url);
            setStatus('done');
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : "An unknown error occurred during rendering.";
            console.error("Rendering failed:", err);
            setError(errorMessage);
            setStatus('error');
        }
    };

    const handleCloudRender = async () => {
        if (!project.timeline || project.timeline.clips.length === 0) {
            setError("Cannot render an empty timeline.");
            setStatus('error');
            return;
        }

        // Debug: Log the user email being used for render
        console.log('🎬 Starting cloud render with user email:', userEmail);
        console.log('🎬 Authenticated user from context:', user);
        
        if (userEmail === 'guest@loopai.com') {
            console.warn('⚠️ WARNING: Using guest email! User may not be properly authenticated.');
        }

        setStatus('cloud-uploading');
        setError(null);
        setMessage("Saving project & uploading assets to cloud...");

        try {
            console.log('🎬 Saving project for user:', userEmail);
            const saveResult = await saveProjectToBackend(project, userEmail);
            
            if (!saveResult.success) {
                throw new Error(saveResult.error || "Failed to upload project assets. Cloud render cannot proceed.");
            }
            
            const projectForRender = saveResult.optimizedProject || project;

            setMessage("Starting cloud render job...");
            console.log('🎬 Starting render job for user:', userEmail);

            const startResult = await startCloudRender(projectForRender, userEmail);
            
            if (!startResult.success || !startResult.jobId) {
                throw new Error(startResult.error || "Failed to start cloud render. Please try again.");
            }

            setStatus('cloud-processing');
            setMessage("Job queued. Waiting for cloud server...");

            const pollResult = await pollRenderUntilComplete(
                startResult.jobId,
                (statusUpdate) => {
                    if (statusUpdate.status === 'processing') {
                        setMessage("Cloud server is rendering your video... This may take a few minutes.");
                    } else if (statusUpdate.status === 'pending') {
                        setMessage("Job queued for rendering...");
                    }
                },
                600
            );

            if (pollResult.success && pollResult.url) {
                setVideoUrl(pollResult.url);
                setStatus('done');
                
                // Auto-save project with video URL and completed status
                // This enables "View Video" functionality in the User Dashboard
                try {
                    const updatedProject = {
                        ...project,
                        renderedVideoUrl: pollResult.url,
                        video_url: pollResult.url,
                        status: 'completed',
                        lastModified: Date.now()
                    };
                    onProjectUpdate(updatedProject);
                    await saveProjectToBackend(updatedProject, userEmail);
                    console.log('✅ Project saved with completed status and video URL');
                } catch (saveErr) {
                    console.warn('⚠ Could not auto-save project with video URL:', saveErr.message);
                }
            } else {
                throw new Error(pollResult.error || "Cloud rendering failed.");
            }

        } catch (err) {
            console.error("Cloud render error:", err);
            setError(err.message || "Failed to render video.");
            setStatus('error');
        }
    };
    
    const handleDownload = () => {
        if (!videoUrl) return;
        const a = document.createElement('a');
        a.href = videoUrl;
        a.download = `${project.name.replace(/ /g, '_')}_render.mp4`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleSaveToProject = async () => {
        if (!videoUrl) return;
        setIsSavingToProject(true);
        try {
            const newAsset = {
                id: `asset-video-${uuidv4()}`,
                type: 'video',
                name: `Rendered Video - ${new Date().toLocaleString()}.mp4`,
                data: videoUrl
            };
            
            // Update project with video URL and completed status
            const updatedProject = {
                ...project,
                assets: [...project.assets, newAsset],
                renderedVideoUrl: videoUrl,
                video_url: videoUrl,
                status: 'completed',
                lastModified: Date.now()
            };
            
            onProjectUpdate(updatedProject);
            
            const result = await saveProjectToBackend(updatedProject, userEmail);
            
            if (result.success) {
                alert("Video saved to project assets successfully!");
                if (result.optimizedProject) {
                    onProjectUpdate(result.optimizedProject);
                }
            } else {
                throw new Error(result.error);
            }
        } catch (e) {
            console.error(e);
            alert(`Failed to save video to project: ${e.message}`);
        } finally {
            setIsSavingToProject(false);
        }
    };

    const renderContent = () => {
        switch (status) {
            case 'idle':
                return (
                    <div className="text-center">
                        <h3 className="text-2xl font-bold mb-4">Export Video</h3>
                        <p className="text-gray-400 mb-8">Choose a rendering method. Cloud rendering ensures best quality but requires a backend.</p>
                        <div className="flex flex-col gap-4">
                            <button
                                onClick={handleCloudRender}
                                className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-lg text-lg flex items-center justify-center mx-auto w-full transition-colors"
                            >
                                <FilmIcon className="w-6 h-6 mr-3" />
                                Cloud Render (Recommended)
                            </button>
                            <p className="text-xs text-gray-500 -mt-2">Requires backend setup</p>
                            
                            <button
                                onClick={handleLocalRender}
                                className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg text-lg flex items-center justify-center mx-auto w-full transition-colors"
                            >
                                <MagicWandIcon className="w-6 h-6 mr-3" />
                                Browser Render (Local)
                            </button>
                            <p className="text-xs text-yellow-500 -mt-2">⚠️ Limited browser capabilities</p>
                        </div>
                    </div>
                );

            case 'rendering':
            case 'cloud-uploading':
            case 'cloud-processing':
                return (
                    <div className="text-center">
                        <LoadingSpinner className="w-16 h-16 mx-auto mb-4" />
                        <h3 className="text-2xl font-semibold mb-2">Rendering...</h3>
                        <p className="text-gray-400 mb-4">{message}</p>
                        {progress > 0 && (
                            <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                                <div className="bg-purple-600 h-full transition-all" style={{ width: `${progress}%` }} />
                            </div>
                        )}
                    </div>
                );

            case 'done':
                return (
                    <div className="text-center">
                        <h3 className="text-2xl font-bold mb-4 text-green-400">Rendering Complete!</h3>
                        {videoUrl && (
                            <video src={videoUrl} controls className="w-full rounded-lg mb-4" />
                        )}
                        <div className="flex gap-4 justify-center">
                            <button
                                onClick={handleDownload}
                                className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-2 px-6 rounded-lg flex items-center"
                            >
                                <DownloadIcon className="w-5 h-5 mr-2" />
                                Download
                            </button>
                            <button
                                onClick={handleSaveToProject}
                                disabled={isSavingToProject}
                                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg flex items-center"
                            >
                                <SaveIcon className="w-5 h-5 mr-2" />
                                {isSavingToProject ? 'Saving...' : 'Save to Project'}
                            </button>
                        </div>
                    </div>
                );

            case 'error':
                return (
                    <div className="text-center">
                        <h3 className="text-2xl font-bold mb-4 text-red-400">Rendering Failed</h3>
                        <p className="text-gray-400 mb-6">{error}</p>
                        <button
                            onClick={() => setStatus('idle')}
                            className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg"
                        >
                            Try Again
                        </button>
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-[#181820] border border-gray-700 rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-4 flex justify-between items-center border-b border-gray-700">
                    <h2 className="text-xl font-bold">Render Video</h2>
                    <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-700">
                        <CloseIcon className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-8 flex-grow overflow-y-auto">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};
